const STAMP_LINE_RE = /^\*\*[^*\n]+\*\* · .+$/;

export function formatNotesStampTime(at: Date): string {
  const formatted = at.toLocaleString('en-GB', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${formatted} UTC`;
}

export function formatNotesStamp(author: string, at: Date): string {
  const name = author.trim() || 'Unknown';
  return `**${name}** · ${formatNotesStampTime(at)}`;
}

export function startsWithNotesStamp(text: string): boolean {
  const firstLine = text.trim().split('\n', 1)[0] || '';
  return STAMP_LINE_RE.test(firstLine);
}

function firstLine(text: string): string {
  return text.trim().split('\n', 1)[0] || '';
}

function afterLeadingStamp(text: string, stamp?: string): string {
  const trimmed = text.trim();
  const header = stamp && trimmed.startsWith(stamp) ? stamp : firstLine(trimmed);
  if (!STAMP_LINE_RE.test(header)) return trimmed;
  if (!trimmed.startsWith(header)) return trimmed;
  return trimmed.slice(header.length).replace(/^\n+/, '');
}

export function latestNotesCommentBody(notes: string): string {
  const text = notes.trim();
  if (!text) return '';

  const lines = text.split('\n');
  const stampIndexes: number[] = [];
  for (let i = 0; i < lines.length; i += 1) {
    if (STAMP_LINE_RE.test(lines[i])) stampIndexes.push(i);
  }

  if (stampIndexes.length === 0) return text;

  if (stampIndexes[0] > 0) {
    const prefix = lines.slice(0, stampIndexes[0]).join('\n').trim();
    if (prefix) return prefix;
  }

  for (let i = 0; i < stampIndexes.length; i += 1) {
    const start = stampIndexes[i];
    const end = stampIndexes[i + 1] ?? lines.length;
    const body = lines.slice(start + 1, end).join('\n').trim();
    if (body) return body;
  }

  return '';
}

export function latestNotesCommentPreview(notes: string): string {
  return latestNotesCommentBody(notes).replace(/\s+/g, ' ').trim();
}

export function isStampOnly(text: string, stamp?: string): boolean {
  return !afterLeadingStamp(text, stamp).trim() && startsWithNotesStamp(text);
}

export function stripUnusedStamp(current: string, baseline: string, stamp?: string): string {
  const text = current.trim();
  const original = baseline.trim();
  if (!text) return '';
  if (!startsWithNotesStamp(text)) return text;
  if (stamp && !text.startsWith(stamp) && firstLine(text) !== stamp) {
    return text;
  }

  const after = afterLeadingStamp(text, stamp);
  if (!after) return original;
  if (after === original) return original;
  if (original && after.endsWith(original)) {
    const body = after.slice(0, after.length - original.length).trim();
    if (!body) return original;
    const header = stamp && text.startsWith(stamp) ? stamp : firstLine(text);
    return `${header}\n\n${body}\n\n${original}`;
  }
  return text;
}

export function prependStampedComment(
  history: string,
  comment: string,
  author: string,
  at: Date,
): string {
  const body = comment.trim();
  if (!body) return history.trim();
  if (isStampOnly(body)) return history.trim();
  const block = startsWithNotesStamp(body)
    ? body
    : `${formatNotesStamp(author, at)}\n\n${body}`;
  const existing = history.trim();
  return existing ? `${block}\n\n${existing}` : block;
}

export function commitNotes(
  current: string,
  baseline: string,
  author: string,
  at: Date,
): string {
  const original = baseline.trim();
  const text = stripUnusedStamp(current, original).trim();
  if (!text) return '';
  if (text === original) return original;

  if (!original) {
    return startsWithNotesStamp(text) ? text : prependStampedComment('', text, author, at);
  }

  if (text.endsWith(original)) {
    const prefix = text.slice(0, text.length - original.length).trim();
    if (!prefix || isStampOnly(prefix)) return original;
    return prependStampedComment(original, prefix, author, at);
  }

  return text;
}

export function notesStampParagraph(author: string, at: Date) {
  const name = author.trim() || 'Unknown';
  return {
    type: 'paragraph' as const,
    content: [
      { type: 'text' as const, text: name, marks: [{ type: 'bold' as const }] },
      { type: 'text' as const, text: ` · ${formatNotesStampTime(at)}` },
    ],
  };
}
